import { makeAutoObservable } from 'mobx';
import {
  BodyMeasurementUnit,
  DistanceUnit,
  DistanceUnitShort,
  isBodyMeasurementUnit,
  isDistanceUnit,
  isWeightUnit,
  RepInputType,
  UserPreferences as TUserPreferences,
  Weekday,
  WeightUnit,
} from 'hevy-shared';
import API from 'utils/API';
import { languageState, setWebLanguage } from 'translations/t';
import { Language, isLanguage } from 'hevy-shared/translations';

const USER_PREFERENCES_LOCAL_STORAGE_KEY = 'USER_PREFERENCES_LOCAL_STORAGE_KEY';
const LOCAL_USER_PREFERENCES_KEY = 'LOCAL_USER_PREFERENCES_KEY';

type WebUserPreferences = Omit<
  TUserPreferences,
  'username' | 'workout_keep_awake' | 'disabled_review_request' | 'rep_input_type'
>;

interface LocalUserPreferences {
  rep_input_type?: RepInputType;
}

export class UserPreferences {
  private weight_unit?: WeightUnit;
  private distance_unit?: DistanceUnit;
  private body_measurement_unit?: BodyMeasurementUnit;
  private first_weekday?: Weekday;
  private default_rest_timer_seconds?: number;
  private rep_input_type?: RepInputType;
  private volume_includes_warmup_sets?: boolean;

  constructor() {
    makeAutoObservable(this);
  }

  /** Do not pass this in as a locale argument to dayjs or JS built-in functions.
   * Dayjs and JS expect locale to be formmated as langCode-regionCode but we
   * format locales as langCode_regionCode
   */
  get language(): Language {
    return languageState.current;
  }

  /**
   * Returns the language following the format that JS expects locale arguments to follow
   * For langauges with regions i.e {langugageCode}_{regionCode}: pt_BR, zh_CN, zh_TW, the
   * expected format is {langugageCode}-{regionCode} with an exception for pt_PT which is just 'pt'
   * For other languages the languageCode alone suffices
   */
  get languageFormattedForJs(): string {
    return this.language === 'pt_PT' ? 'pt' : this.language.replace('_', '-');
  }

  get weightUnit(): WeightUnit {
    return this.weight_unit || 'kg';
  }

  get distanceUnit(): DistanceUnit {
    return this.distance_unit || 'kilometers';
  }

  get volumeIncludesWarmupSets(): boolean {
    return this.volume_includes_warmup_sets || false;
  }

  get distanceUnitShort(): DistanceUnitShort {
    return this.distanceUnit === 'kilometers' ? 'km' : 'mi';
  }

  get bodyMeasurementUnit(): BodyMeasurementUnit {
    return this.body_measurement_unit || 'cm';
  }

  get firstWeekday(): Weekday {
    return this.first_weekday || 'sunday';
  }

  get defaultRestTimerSeconds(): number {
    return this.default_rest_timer_seconds || 0;
  }

  get repInputType(): RepInputType {
    return this.rep_input_type || 'reps';
  }

  setRepInputType = (repInputType: RepInputType) => {
    this.rep_input_type = repInputType;
    window.localStorage.setItem(
      LOCAL_USER_PREFERENCES_KEY,
      JSON.stringify({ rep_input_type: repInputType }),
    );
  };

  update = async (p: Omit<TUserPreferences, 'username'>) => {
    if (p.weight_unit && isWeightUnit(p.weight_unit)) {
      this.weight_unit = p.weight_unit;
    }

    if (p.distance_unit && isDistanceUnit(p.distance_unit)) {
      this.distance_unit = p.distance_unit;
    }

    if (p.body_measurement_unit && isBodyMeasurementUnit(p.body_measurement_unit)) {
      this.body_measurement_unit = p.body_measurement_unit;
    }

    if (p.language && isLanguage(p.language)) {
      setWebLanguage(p.language);
    }

    await API.updateUserPreferences(p);
  };

  fetch = async () => {
    const result = await API.getUserPreferences();
    this.updateModel(result.data);
    window.localStorage.setItem(USER_PREFERENCES_LOCAL_STORAGE_KEY, JSON.stringify(result.data));
  };

  hydrate = () => {
    const json = window.localStorage.getItem(USER_PREFERENCES_LOCAL_STORAGE_KEY);
    if (json) {
      this.updateModel(JSON.parse(json));
    }

    const localUserPreferences = window.localStorage.getItem(LOCAL_USER_PREFERENCES_KEY);
    if (localUserPreferences) {
      this.updateLocalModel(JSON.parse(localUserPreferences));
    }
  };

  clearData = () => {
    window.localStorage.removeItem(USER_PREFERENCES_LOCAL_STORAGE_KEY);
    window.localStorage.removeItem(LOCAL_USER_PREFERENCES_KEY);

    const emptyUserPreferences: WebUserPreferences = {};
    this.updateModel(emptyUserPreferences);

    const emptyLocalUserPreferences: LocalUserPreferences = {};
    this.updateLocalModel(emptyLocalUserPreferences);
  };

  private updateModel = (userPreferences: WebUserPreferences) => {
    this.weight_unit = userPreferences.weight_unit;
    this.distance_unit = userPreferences.distance_unit;
    this.body_measurement_unit = userPreferences.body_measurement_unit;
    this.first_weekday = userPreferences.first_weekday;
    this.default_rest_timer_seconds = userPreferences.default_rest_timer_seconds;
    this.volume_includes_warmup_sets = userPreferences.volume_includes_warmup_sets;

    if (userPreferences.language && isLanguage(userPreferences.language)) {
      setWebLanguage(userPreferences.language);
    }
  };

  private updateLocalModel = (localUserPreferences: LocalUserPreferences) => {
    this.rep_input_type = localUserPreferences.rep_input_type;
  };
}
